import React, { useCallback, useContext, useMemo } from 'react'
import { P, match } from 'ts-pattern'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2, OFFER_TYPE_CRUISE, OFFER_TYPE_BUNDLE_AND_SAVE } from 'constants/offer'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LEOfferCard from './LEOfferCard'
import TourOfferCard from './TourOfferCard'
import TourV2OfferCard from './TourV2OfferCard'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import CruiseOfferCard from './CruiseOfferCard'
import BedbankOfferCard from './BedbankOfferCard'
import BundleAndSaveOfferCard from './BundleAndSaveOfferCard'
import offerPageURL from 'lib/offer/offerPageURL'
import VillaOfferCard from './VillaOfferCard'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLoggedIn } from 'selectors/accountSelectors'
import * as Analytics from 'analytics/analytics'
import { clickWalledContentTile } from 'analytics/optimizely/events'

interface Props {
  idx?: number;
  className?: string;
  offer: App.AnyOffer;
  additionalOfferUrlParams?: string;
}

function OfferCardMapper(props: Props) {
  const { offer, idx, className, additionalOfferUrlParams } = props
  const userLoggedIn = useAppSelector(isLoggedIn)
  const onEvent = useContext(OfferListEventsContext)
  const onClick = useCallback(() => {
    onEvent(OfferListEvents.productClick, { offer, position: idx, key: EventDataKey.ProductClick })

    if (offer.parentType === 'hotel' && offer.walledGarden && !userLoggedIn) {
      Analytics.track('click-walled-content-tile', clickWalledContentTile())
    }
  }, [idx, offer, onEvent, userLoggedIn])

  const offerUrl = useMemo(() => {
    return offerPageURL(offer) + (additionalOfferUrlParams ? `?${additionalOfferUrlParams}` : '')
  }, [offer, additionalOfferUrlParams])

  return match(offer)
    .with({
      type: P.union(
        OFFER_TYPE_HOTEL,
        OFFER_TYPE_LAST_MINUTE,
        OFFER_TYPE_ALWAYS_ON,
      ),
    }, (item) => (
      <LEOfferCard
        offer={item}
        onClick={onClick}
        className={className}
        offerUrl={offerUrl}
      />
    ))
    .with({ type: OFFER_TYPE_BUNDLE_AND_SAVE }, (item) => (
      <BundleAndSaveOfferCard
        offer={item as App.BundleOffer | App.BundleOfferSummary}
        onClick={onClick}
        className={className}
        offerUrl={offerUrl}
      />
    ))
    .with({ type: OFFER_TYPE_BED_BANK }, (item) => (
      <BedbankOfferCard
        offer={item}
        onClick={onClick}
        className={className}
        offerUrl={offerUrl}
      />
    ))
    .with({ type: OFFER_TYPE_TOUR }, (item) => (
      <TourOfferCard
        offer={item}
        onClick={onClick}
        className={className}
        offerUrl={offerUrl}
      />
    ))
    .with({ parentType: OFFER_TYPE_TOUR_V2 }, (item) => (
      <TourV2OfferCard
        offer={item}
        onClick={onClick}
        className={className}
        offerUrl={offerUrl}
      />
    ))
    .with({ parentType: OFFER_TYPE_CRUISE }, (item) => (
      <CruiseOfferCard
        offer={item}
        onClick={onClick}
        className={className}
        offerUrl={offerUrl}
      />
    ))
    .with({ parentType: 'hotel', type: 'rental' }, (item) => (
      <VillaOfferCard
        offer={item as unknown as App.VillaOffer | App.VillaOfferSummary}
        onClick={onClick}
        className={className}
        offerUrl={offerUrl}
      />
    ))
    .otherwise(() => null)
}

export default OfferCardMapper
